import type { Trigger } from '@radix-ui/react-select';
import type { ComponentPropsWithoutRef, ElementRef } from 'react';

import type { DataAttributesProps } from '../../../utils/types';
import type { SelectOption } from '../common';

import { forwardRef, useId } from 'react';

import {
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
  Select as ShadSelect,
} from '../../../ui/select';

type SelectOptionGroup = {
  label: string;
  options: SelectOption[];
};

export const selectEmptyOptions = [
  { label: '-', value: '-' },
] satisfies SelectOption[];

export type SelectProps = DataAttributesProps &
  Omit<
    ComponentPropsWithoutRef<typeof SelectTrigger>,
    'children' | 'onChange' | 'value'
  > & {
    name?: string;
    onChange: (value: string) => void;
    placeholder?: string;
    value?: string;
  } & (
    | {
        options: SelectOption[];
        optionsGroups?: never;
      }
    | {
        options?: never;
        optionsGroups: SelectOptionGroup[];
      }
  );

export const Select = forwardRef<ElementRef<typeof Trigger>, SelectProps>(
  (
    {
      className,
      'data-testid': dataTestId,
      disabled,
      name,
      onChange,
      options,
      optionsGroups,
      placeholder,
      value,
      ...rest
    },
    ref
  ) => {
    const id = useId();
    const testId = dataTestId ?? name;

    return (
      <ShadSelect disabled={disabled} value={value} onValueChange={onChange}>
        <SelectTrigger
          ref={ref}
          className={className}
          data-testid={testId}
          {...rest}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className="max-w-[var(--radix-select-trigger-width)]">
          {!!options &&
            options.map((option) => (
              <SelectItem
                key={`${id}_${option.value}`}
                className="whitespace-normal break-words"
                data-testid={`${testId}-${option.value}`}
                value={option.value}
              >
                <span className="flex items-center gap-4">
                  {option.indicator}
                  {option.label}
                </span>
              </SelectItem>
            ))}

          {!!optionsGroups &&
            optionsGroups.map((group, groupIndex) => (
              <SelectGroup key={`${id}_group_${groupIndex}`}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.options.map((option) => (
                  <SelectItem
                    key={`${id}_${option.value}`}
                    data-testid={`${testId}-${option.value}`}
                    value={option.value}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
        </SelectContent>
      </ShadSelect>
    );
  }
);

Select.displayName = 'Select';
